import { api } from '@sd/contracts';
import type { ApiClient } from './http';
import { ObjectUrlCache } from './objectUrls';

/**
 * Photos no longer shown that are kept for reuse; beyond this the least recently used is dropped
 * and its object URL revoked. A stored photo is a 256 × 256 WebP of 10–20 KB, so this keeps about
 * a megabyte for going back and forth between lists. Photos on screen are never revoked: a member
 * list of 500 people shows 500 of them, and each is fetched once.
 */
export const IDLE_AVATARS = 64;
/** Requests at once, so opening a long list does not queue every other request behind its photos. */
const MAX_FETCHES = 6;

/**
 * Profile photos fetched with the session's credentials and shown as object URLs: the desktop
 * renderer (`sd-app://app`) calls servers with a bearer token, which an `<img src>` cannot send.
 * Keyed by person and version, so a new photo is a new entry and a cached one is never stale.
 */
export class AvatarCache {
  private readonly cache: ObjectUrlCache;

  constructor(
    private readonly api: ApiClient,
    now: () => number = Date.now,
  ) {
    this.cache = new ObjectUrlCache({ idle: IDLE_AVATARS, maxFetches: MAX_FETCHES, now });
  }

  /** Object URL of the photo; call `releaseAvatar` once it is no longer shown. */
  avatarUrl(userId: string, version: string): Promise<string> {
    return this.cache.get(`${userId}/${version}`, async () => (await this.api.call(api.users.avatar, { params: { userId, version } })).blob());
  }

  /** A view stopped showing it: it stays among the idle ones, and the least recently used beyond IDLE_AVATARS are revoked. */
  releaseAvatar(userId: string, version: string) {
    this.cache.release(`${userId}/${version}`);
  }

  clear() {
    this.cache.clear();
  }
}
